import axios, { AxiosError, AxiosInstance } from 'axios';
import qs from 'qs';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BASE_URL } from 'utils/constants/Api';
import { RequestStatus } from 'utils/constants/RequestStatus';
import { FilterContext } from 'utils/context';

const WAIT_TIME_LIMIT = 600_000; // 10 mins

//* Custom axios instance to ensure POST request ever
const customAxios = axios.create({
  baseURL: BASE_URL,
  timeout: WAIT_TIME_LIMIT,
  withCredentials: true,
  method: 'POST',
});

const getStatusFromError = (error: AxiosError) => {
  if (error.code === 'ECONNABORTED') {
    return RequestStatus.Timeout;
  }
  return error.response?.status === 403
    ? RequestStatus.PermissionError
    : RequestStatus.Failure;
};

interface IUseSignedUrl {
  doFetch: () => Promise<void>;
  data: string;
  status: RequestStatus;
  isError: boolean;
  isLoading: boolean;
  exportType: 'async' | 'sync';
}

function useSignedUrl(httpClient: AxiosInstance = customAxios): IUseSignedUrl {
  const history = useHistory();
  const { getShipmentsRequest } = useContext(FilterContext);
  const requestPayload = getShipmentsRequest();
  const [data, setData] = useState('');
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.Loading);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { downloadFile: taskExecutionId } = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  });

  const doFetch = async () => {
    setStatus(RequestStatus.Loading);
    setIsError(false);
    setIsLoading(true);
    try {
      const response = taskExecutionId
        ? await httpClient.post(
            '/file-export/task-signed-url',
            {
              taskExecutionId,
            },
            { withCredentials: true },
          )
        : await httpClient.post<{
            signedUrl: string;
            isExpired: boolean;
            recipientEmail: string;
          }>('/file-export/signed-url', requestPayload, {
            withCredentials: true,
          });
      setData(response.data.signedUrl);

      if (response.data.isExpired) {
        setStatus(RequestStatus.ExpirationError);
      } else {
        setStatus(RequestStatus.Success);
      }

      setIsLoading(false);
    } catch (error) {
      setStatus(getStatusFromError(error as AxiosError));
      setIsError(true);
      setIsLoading(false);
    }
  };

  return {
    doFetch,
    data,
    status,
    isError,
    isLoading,
    exportType: taskExecutionId ? 'async' : 'sync',
  };
}

export default useSignedUrl;
